import type { OnnxRuntime, InferenceSession, Tensor } from './vendor/platform/runtime.js';
import { setRuntime } from './vendor/platform/runtime.js';

let _bootstrapped = false;

/**
 * Resolve ORT thread count based on cross-origin isolation state.
 *
 * @param crossOriginIsolated - Whether the page is cross-origin isolated
 *   (SharedArrayBuffer available). Corresponds to `globalThis.crossOriginIsolated`.
 * @param hardwareConcurrency - Physical CPU thread count (`navigator.hardwareConcurrency`).
 *   Callers should default to 2 when undefined.
 * @returns Thread count: min(hardwareConcurrency, 4) when isolated, otherwise 1.
 *
 * @internal — exported for unit testing (pure function, no side effects).
 */
export function _resolveNumThreads(
  crossOriginIsolated: boolean,
  hardwareConcurrency: number,
): number {
  return crossOriginIsolated ? Math.min(hardwareConcurrency, 4) : 1;
}

export function onnxRuntimeBootstrap(): void {
  if (_bootstrapped) return;
  _bootstrapped = true;

  void import('onnxruntime-web')
    .then((ort) => {
      // ── WASM + JSEP configuration ──────────────────────────────────────
      // Using wasmPaths as an object (not a string) so we can redirect
      // `ort-wasm-simd-threaded.jsep.mjs` → `ort-wasm-simd-threaded.jsep.js`.
      //
      // Root cause: S3/CloudFront serves .mjs files as application/octet-stream
      // which browsers reject for module scripts (HTML spec strict MIME checking).
      // S3 serves .js files as application/javascript (correct).
      //
      // ORT reads wasmPaths BEFORE calling createSession, so this redirect
      // takes effect before ORT tries to load the JSEP module.
      //
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (ort.env.wasm as any).wasmPaths = {
        // WASM binaries — served from /ort/ (copied by vite-plugin-static-copy)
        'ort-wasm.wasm':                        '/ort/ort-wasm.wasm',
        'ort-wasm-simd.wasm':                   '/ort/ort-wasm-simd.wasm',
        'ort-wasm-simd-threaded.wasm':          '/ort/ort-wasm-simd-threaded.wasm',
        'ort-wasm-simd-threaded.asyncify.wasm': '/ort/ort-wasm-simd-threaded.asyncify.wasm',
        'ort-wasm-simd-threaded.jsep.wasm':     '/ort/ort-wasm-simd-threaded.jsep.wasm',
        'ort-wasm-simd-threaded.jspi.wasm':     '/ort/ort-wasm-simd-threaded.jspi.wasm',
        // CRITICAL: redirect .jsep.mjs → .jsep.js to bypass S3 MIME type issue.
        // The .js stub exports an empty module; ORT's optional JSEP checks return
        // undefined and ORT gracefully falls back to WASM EP.
        'ort-wasm-simd-threaded.jsep.mjs':      '/ort/ort-wasm-simd-threaded.jsep.js',
      };

      // `|| 2` (not `?? 2`) so that 0 also falls back. Some Android WebViews
      // report `navigator.hardwareConcurrency === 0`; passing 0 to ORT would set
      // numThreads = 0 and crash the runtime.
      const threads = _resolveNumThreads(
        typeof crossOriginIsolated !== 'undefined' && crossOriginIsolated,
        navigator.hardwareConcurrency || 2,
      );
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ORT typings expose these as any
      (ort.env.wasm as any).numThreads = threads;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (ort.env.wasm as any).proxy = true;

      const runtime: OnnxRuntime = {
        async createSession(
          source: string | ArrayBuffer | Uint8Array,
          options?: { executionProviders?: string[] },
        ): Promise<InferenceSession> {
          const session = await ort.InferenceSession.create(source as Uint8Array, {
            executionProviders: options?.executionProviders ?? ['wasm'],
          });
          return session as unknown as InferenceSession;
        },

        createTensor(data: Float32Array | Int32Array | Uint8Array, dims: readonly number[]): Tensor {
          const type =
            data instanceof Float32Array ? 'float32' :
            data instanceof Int32Array   ? 'int32'   : 'uint8';
          const t = new ort.Tensor(type, data, dims as number[]);
          return t as unknown as Tensor;
        },
      };

      setRuntime(runtime);
    })
    .catch((err: unknown) => {
      console.error('[onnxRuntimeBootstrap] Failed to load onnxruntime-web:', err);
      _bootstrapped = false; // allow retry on next call
    });
}
